<script lang="ts">
	import {
		AdvancedCard,
		Alert,
		CardGrid,
		Container,
		Modal,
		Spinner,
		Text,
	} from '@Shelly/shelly-design-system';
	import { _ } from 'svelte-i18n';
	import HomeAvatar from '../components/HomeAvatar.svelte';
	import CrazyDivider from '../components/CrazyDivider.svelte';
	import { i18nKeyForError, type ErrorCode } from '../lib/errors';
	import type { AvailableUnits } from '../services/auth';

	interface Props {
		email: string;
		availableUnits: AvailableUnits;
		loading: boolean;
		error: ErrorCode | null;
		show: boolean;
		onpick: (unitId: number) => void;
		onback: () => void;
	}

	let {
		email,
		availableUnits,
		loading,
		error,
		show = $bindable(false),
		onpick,
		onback,
	}: Props = $props();

	// Mirrors the active-unit hiding logic from UnitSelectionModal.svelte: we
	// don't have an active unit during OAuth, so this is always the full set.
	const unitEntries = $derived(Object.entries(availableUnits));
	const subUnitEntries: [string, string][] = [];
	const hasUnits = $derived(unitEntries.length > 0);
	const hasSubUnits = subUnitEntries.length > 0;
	const showSectionLabels = $derived(hasUnits && hasSubUnits);

	// Tracks the unit the user just clicked so the AdvancedCard stays
	// highlighted while the /oauth/login request is in flight.
	let selectedId = $state<string | undefined>(undefined);

	const onCloseModal = () => {
		if (loading) return;
		show = false;
		selectedId = undefined;
		onback();
	};

	const onSelectUnit = (unitId: string) => {
		if (loading) return;
		selectedId = unitId;
		onpick(Number(unitId));
	};
</script>

<Modal
	bind:showModal={show}
	title={$_('select_unit_from_account')}
	on:close={onCloseModal}
	removeScrollPadding
	backdropClose={!loading}
>
	<Container slot="content" padding="scrollPaddings">
		<div class="position-relative">
			<div class="unit-modal-account mb-m">
				<Text noWrap styleType="body">{email}</Text>
			</div>
			<CrazyDivider />

			{#if error}
				<Alert
					type="danger"
					heading={$_('error_heading')}
					message={$_(i18nKeyForError(error))}
					class="mt-m"
				/>
			{/if}

			<div class="unit-modal-homes mt-m">
				{#if hasUnits}
					{#if showSectionLabels}
						<Text class="mb-s" styleType="label-large">
							{$_('unit_section_your_homes')}
						</Text>
					{/if}
					<CardGrid gridGap="s" sm={200} md={200} xl={200} noPaddingBottom>
						{#each unitEntries as [unitId, unitName] (unitId)}
							<AdvancedCard
								title={unitName}
								isCondensed
								isFocusable
								selected={selectedId === unitId}
								class="width-full"
								aria-label={$_('label_select_home', { values: { name: unitName } })}
								on:click={() => onSelectUnit(unitId)}
							>
								<HomeAvatar slot="left" home={{ id: unitId, name: unitName }} size="m" />
							</AdvancedCard>
						{/each}
					</CardGrid>
				{/if}

				{#if hasSubUnits}
					<Text class={hasUnits ? 'mt-m mb-s' : 'mb-s'} styleType="label-large">
						{$_('unit_section_shared_homes')}
					</Text>
					<CardGrid gridGap="s" sm={200} md={200} xl={200} noPaddingBottom>
						{#each subUnitEntries as [unitId, unitName] (unitId)}
							<AdvancedCard
								title={unitName}
								isCondensed
								isFocusable
								selected={selectedId === unitId}
								class="width-full"
								aria-label={$_('label_select_home', { values: { name: unitName } })}
								on:click={() => onSelectUnit(unitId)}
							>
								<HomeAvatar slot="left" home={{ id: unitId, name: unitName }} size="m" />
							</AdvancedCard>
						{/each}
					</CardGrid>
				{/if}
			</div>

			{#if loading}
				<div class="unit-modal-loading-overlay">
					<Spinner size="m" />
				</div>
			{/if}
		</div>
	</Container>
</Modal>

<style>
	.unit-modal-account {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.unit-modal-loading-overlay {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--color-background-primary-transparent, rgba(0, 0, 0, 0.4));
		z-index: 1;
		border-radius: var(--border-radius-m, 8px);
	}
</style>
